import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { List, ListItem, ListItemIcon, ListItemText, Paper, TextField, Button, Typography, Breadcrumbs, Link, Menu, MenuItem, Switch, FormControlLabel, FormControl, InputLabel, Select } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid } from '@mui/x-data-grid';
import { MdAccessTime as AccessTimeIcon, MdFolder as FolderIcon, MdPerson as PersonIcon, MdNavigateNext as NavigateNextIcon, MdInsertDriveFile as FileIcon, MdAdd as AddIcon, MdSearch as SearchIcon } from "react-icons/md";
import { IconButton, Tooltip, Box, Chip } from '@mui/material';
import { MdCreateNewFolder as CreateNewFolderIcon, MdNoteAdd as CreateFileIcon, MdMoreVert as MoreVertIcon } from "react-icons/md";
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import PageLayout from './ui/PageLayout';
import api, { enhancedApi, API_BASE_URL } from '../api';
import ESignaturePrepare from './ESignaturePrepare';

const FolderFileExplorer = () => {
  const { clientId, folderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const handleOptionSelect = (option) => {
    console.log('FolderFileExplorer received option:', option);
    // Handle different options from sidebar
    switch (option) {
      case 'addClient':
        console.log('Navigate to add client');
        break;
      case 'clientRequest':
        console.log('Navigate to client request');
        break;
      case 'email':
        console.log('Navigate to email');
        break;
      case 'clientSidebar':
        console.log('Toggle client sidebar');
        break;
      case 'notifications':
        console.log('Show notifications');
        break;
      default:
        console.log('Unhandled option:', option);
    }
  };

  const [viewingClients, setViewingClients] = useState(true);
  const [viewingFolderFiles, setViewingFolderFiles] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileToView, setFileToView] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [currentClientName, setCurrentClientName] = useState('');
  const [currentClientPath, setCurrentClientPath] = useState([]);
  const [currentClient, setCurrentClient] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState('');
  const [currentDirectory, setCurrentDirectory] = useState('Client files');
  const [folderNameInput, setFolderNameInput] = useState('');
  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [filePreview, setFilePreview] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [normalizedSignatureList, setNormalizedSignatureList] = useState([]);
  const [selectedSignatures, setSelectedSignatures] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFolderPath, setCurrentFolderPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [moveClients, setMoveClients] = useState([]);
  const [moveFolders, setMoveFolders] = useState([]);
  const [selectedMoveClient, setSelectedMoveClient] = useState(null);
  const [selectedMoveFolder, setSelectedMoveFolder] = useState(null);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState(null);
  // E-signature status map for files: { "file_db_id": { status, status_display, ... } }
  const [eSignStatuses, setESignStatuses] = useState({});
  const [eSignPrepareFile, setESignPrepareFile] = useState(null);
  const [eSignDocumentId, setESignDocumentId] = useState(null);
  const [auditLogData, setAuditLogData] = useState([]);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sendFileLinkDialogOpen, setSendFileLinkDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [includeMessage, setIncludeMessage] = useState(false);
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');
  const [movingFileId, setMovingFileId] = useState(null);

  const updatedDefaultLayoutPluginInstance = defaultLayoutPlugin({
    onPageChange: (e) => setCurrentPage(e.currentPage + 1),
  });

  const getAuthHeaders = () => {
    // Get the token from the new multi-tenant auth system
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.access_token) {
          return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userData.access_token}`,
          };
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Fallback to old method if new method fails
    const jwtToken = localStorage.getItem('access_token') || localStorage.getItem('jwtToken');
    if (jwtToken) {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };
    }
    
    // Return empty headers if no token found
    return {
      'Content-Type': 'application/json',
    };
  };

  const getUserId = () => {
    // Get the user ID from the new multi-tenant auth system
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.id || userData.user_id) {
          return userData.id || userData.user_id;
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Fallback: try user_id directly from localStorage
    const fallbackId = localStorage.getItem('user_id');
    if (fallbackId) {
      return fallbackId;
    }

    // Return null if no user ID found
    return null;
  };

  const retryFetch = async (url, options, retries = 3, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, options);
        if (response.ok) return response;
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      } catch (err) {
        if (i === retries - 1) throw err;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const fetchData = async () => {
    console.log('fetchData called - Pathname:', location.pathname, 'ClientId:', clientId, 'FolderId:', folderId, 'Time:', new Date().toLocaleString());
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      if (location.pathname === '/files/clients/root') {
        setViewingClients(true);
        setViewingFolderFiles(false);
        setCurrentDirectory('Client files');
        setCurrentFolderPath([]);
        setCurrentClient(null);
        setCurrentClientPath([]);
        setCurrentClientName('');
        setCurrentFolder(null);
        setCurrentFolderName('');
        await fetchClients();
      } else if (location.pathname.startsWith('/files/clients/') && clientId) {
        setViewingClients(false);
        setViewingFolderFiles(!!folderId);
        setCurrentDirectory('Client files');
        await fetchClientFiles(clientId, folderId, showArchived);
      } else if (location.pathname === '/files/internal/root') {
        setViewingClients(false);
        setViewingFolderFiles(false);
        setCurrentDirectory('Internal files');
        setCurrentFolderPath([]);
        setCurrentClient(null);
        setCurrentClientPath([]);
        setCurrentClientName('');
        setCurrentFolder(null);
        setCurrentFolderName('');
        await fetchFolders();
      } else if (location.pathname.startsWith('/files/internal/folders/') && folderId) {
        setViewingClients(false);
        setViewingFolderFiles(true);
        setCurrentDirectory('Internal files');
        if (!currentFolder || currentFolder.originalId !== parseInt(folderId)) {
          setCurrentFolder({
            id: `folder-${folderId}`,
            originalId: parseInt(folderId),
            name: 'Loading...',
            type: 'folder',
          });
        }
        await fetchInternalFolderContents(folderId);
      } else if (location.pathname === '/files/personal/root') {
        setViewingClients(false);
        setViewingFolderFiles(false);
        setCurrentDirectory('My files');
        setCurrentFolderPath([]);
        setCurrentClient(null);
        setCurrentClientPath([]);
        setCurrentClientName('');
        setCurrentFolder(null);
        setCurrentFolderName('');
        await fetchFolders();
      } else if (location.pathname.startsWith('/files/personal/folders/') && folderId) {
        setViewingClients(false);
        setViewingFolderFiles(true);
        setCurrentDirectory('My files');
        if (!currentFolder || currentFolder.originalId !== parseInt(folderId)) {
          setCurrentFolder({
            id: `folder-${folderId}`,
            originalId: parseInt(folderId),
            name: 'Loading...',
            type: 'folder',
          });
        }
        await fetchPersonalFolderContents(folderId);
      } else if (location.pathname === '/files/recent/root') {
        setViewingClients(false);
        setViewingFolderFiles(false);
        setCurrentDirectory('Recently viewed');
        setCurrentFolderPath([]);
        setCurrentClient(null);
        setCurrentClientPath([]);
        setCurrentClientName('');
        setCurrentFolder(null);
        setCurrentFolderName('');
      } else if (location.pathname.startsWith('/files/internal-files/folders/') && folderId) {
        setViewingClients(false);
        setViewingFolderFiles(true);
        setCurrentDirectory('Internal files');
        if (!currentFolder || currentFolder.originalId !== parseInt(folderId)) {
          setCurrentFolder({
            id: `folder-${folderId}`,
            originalId: parseInt(folderId),
            name: 'Loading...',
            type: 'folder',
          });
        }
        await fetchFolders();
      } else {
        console.warn('Unrecognized route:', location.pathname);
        navigate('/home');
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError(`Error loading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location.pathname, clientId, folderId, showArchived]);

  // Fetch e-signature statuses whenever we are viewing a specific client's files.
  // clientId comes from URL params (useParams) when navigating to a client's file view.
  useEffect(() => {
    if (!clientId) {
      setESignStatuses({});
      return;
    }
    api
      .get(`/api/multi-tenant/clients/${clientId}/esignature-file-statuses/`)
      .then((res) => setESignStatuses(res.data || {}))
      .catch(() => setESignStatuses({}));
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      const response = await enhancedApi.getClients();
      const data = response.data || response;
      const clientRows = data.map(client => ({
        id: `client-${client.id}`,
        originalId: client.id,
        name: client.display_name || client.name || 'Unnamed Client',
        type: 'client',
      }));
      setRows(clientRows);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setRows([]);
      setError(`Error fetching clients: ${error.message}`);
    }
  };

  const fetchClientFiles = async (clientId, folderId = null, includeArchived = false) => {
    console.log('fetchClientFiles called - clientId:', clientId, 'folderId:', folderId);
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const userId = getUserId();
      if (!userId) throw new Error('User ID not found');
      if (!currentClient || currentClient.originalId !== parseInt(clientId)) {
        try {
          const clientResponse = await enhancedApi.getClients();
          const clientsData = clientResponse.data || clientResponse;
          const client = clientsData.find(c => c.id === parseInt(clientId));
          if (client) {
            setCurrentClientName(client.display_name || client.name || 'Unnamed Client');
            setCurrentClient({ id: `client-${client.id}`, originalId: client.id, name: client.display_name || client.name });
            if (!currentClientPath.some(c => c.originalId === client.id)) {
              setCurrentClientPath([...currentClientPath, { id: `client-${client.id}`, originalId: client.id, name: client.display_name || client.name }]);
            }
          } else {
            setError('Client not found');
            setRows([]);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching client data:', error);
          setError('Failed to fetch client data');
          setRows([]);
          setLoading(false);
          return;
        }
      }
      let folderRows = [];
      if (!folderId) {
        try {
          const foldersResponse = await enhancedApi.getClientFolders(clientId, includeArchived);
          const foldersData = foldersResponse.data || foldersResponse;
          folderRows = (foldersData || []).map(folder => ({
            id: `folder-${folder.id}`,
            originalId: folder.id,
            name: folder.name || 'Unnamed Folder',
            type: 'folder',
          }));
        } catch (error) {
          console.error('Error fetching folders:', error);
          setError(`Failed to fetch folders: ${error.message}`);
        }
      }
      let fileRows = [];
      let subFolderRows = [];
      try {
        let filesData;
        if (folderId) {
          const filesResponse = await enhancedApi.getFolderContents(clientId, folderId, includeArchived);
          filesData = filesResponse.data || filesResponse;
        } else {
          // For root level, we need to get files directly from client
          const filesResponse = await enhancedApi.getClientFiles(clientId, includeArchived);
          filesData = filesResponse.data || filesResponse;
        }
        
        console.log('Files/Subfolders API Response:', filesData);
        fileRows = (filesData.files || []).map((file, index) => ({
          id: `file-${file.id || `${Date.now()}-${index}`}`,
          originalId: file.id,
          file_name: file.file_name || 'Unnamed File',
          file_url: file.url || file.file_url,
          file_size: file.file_size || 0,
          type: 'file',
          ai_classification: file.ai_classification || null,
          uploaded_at: file.uploaded_at || file.created_at || null,
          folder_id: file.folder_id ?? folderId,
        }));
        subFolderRows = (filesData.folders || []).map(folder => ({
          id: `folder-${folder.id}`,
          originalId: folder.id,
          name: folder.name || 'Unnamed Folder',
          type: 'folder',
        }));
      } catch (error) {
        console.error('Error fetching files:', error);
        setError(`Failed to fetch files: ${error.message}`);
      }
      const combinedRows = folderId ? [...subFolderRows, ...fileRows] : [...folderRows, ...fileRows];
      setRows(combinedRows);
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      setError(`Error fetching folder contents: ${error.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) {
      setError('Please select a file');
      return;
    }
    const formData = new FormData();
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      return;
    }
    // Append each file to FormData
    Array.from(files).forEach((file) => {
      formData.append('file', file);
    });
    
    // Append folder and client info only once (not per file)
    if (currentFolder) {
      formData.append('folder_id', currentFolder.originalId);
    }
    if (currentClient?.originalId) {
      formData.append('client_id', currentClient.originalId);
    }
    formData.append('user', userId);
    
    try {
      let response;
      if (currentDirectory === 'Internal files') {
        if (currentFolder?.originalId) {
          // Upload to specific internal folder
          response = await enhancedApi.uploadInternalFile(currentFolder.originalId, formData);
        } else {
          // Upload to root internal files
          response = await enhancedApi.uploadFile(formData);
        }
      } else if (currentDirectory === 'My files') {
        if (currentFolder?.originalId) {
          // Upload to specific personal folder
          response = await enhancedApi.uploadPersonalFile(currentFolder.originalId, formData);
        } else {
          // Upload to root personal files (not supported yet)
          throw new Error('Please select a folder to upload files to');
        }
      } else {
        // For client files, we need to upload to the specific client
        if (currentClient?.originalId) {
          response = await enhancedApi.uploadFile(formData);
        } else {
          throw new Error('No client selected for file upload');
        }
      }
      
      if (response.status === 200 || response.status === 201) {
        // Refresh the appropriate data based on current context
        if (currentDirectory === 'My files' && currentFolder) {
          await fetchPersonalFolderContents(currentFolder.originalId);
        } else if (currentDirectory === 'Internal files' && currentFolder) {
          await fetchInternalFolderContents(currentFolder.originalId);
        } else if (currentClient && currentFolder) {
          await fetchClientFiles(currentClient.originalId, currentFolder.originalId, showArchived);
        } else if (currentClient) {
          await fetchClientFiles(currentClient.originalId, null, showArchived);
        } else {
          await fetchFolders();
        }
        alert('File uploaded successfully');
      } else {
        setError('Failed to upload file: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file: ' + error.message);
    }
  };

  const fetchFiles = async (folderId) => {
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      return;
    }
    try {
      try {
        let data;
        if (currentDirectory === 'Internal files') {
          const response = await enhancedApi.getInternalFolderFiles(folderId);
          data = response.data || response;
        } else if (currentDirectory === 'My files') {
          const response = await enhancedApi.getPersonalFolderFiles(folderId);
          data = response.data || response;
        } else {
          setError('Unknown directory type');
          return;
        }
        
        const fileRows = data.map((file, index) => ({
          id: `file-${file.id || `${Date.now()}-${index}`}`,
          originalId: file.id,
          file_name: file.file_name || 'Unnamed File',
          file_url: file.url || file.file_url,
          file_size: file.file_size || 0,
          type: 'file',
          ai_classification: file.ai_classification || null,
          uploaded_at: file.uploaded_at || file.created_at || null,
          folder_id: file.folder_id ?? folderId,
        }));
        setRows(fileRows);
      } catch (error) {
        console.error('Error fetching files:', error);
        setError(`Error fetching files: ${error.message}`);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError(`Error fetching files: ${error.message}`);
    }
  };

  const fetchFolders = async () => {
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      setRows([]);
      return;
    }
    try {
      try {
        let data;
        if (currentDirectory === 'Internal files') {
          const response = await enhancedApi.getInternalFolders();
          data = response.data || response;
        } else if (currentDirectory === 'My files') {
          const response = await enhancedApi.getPersonalFolders();
          data = response.data || response;
        } else {
          setError('Unknown directory type');
          setRows([]);
          return;
        }
        
        if (!data.folders && !data.files) {
          setRows([]);
          setError('No folders or files found');
        } else {
          const folderRows = (data.folders || []).map(folder => ({
            id: `folder-${folder.id}`,
            originalId: folder.id,
            name: folder.name || 'Unnamed Folder',
            type: 'folder',
          }));
          const fileRows = (data.files || []).map((file, index) => ({
            id: `file-${file.id || `${Date.now()}-${index}`}`,
            originalId: file.id,
            file_name: file.file_name || file.name || 'Unnamed File',
            file_url: file.file,
            file_size: file.file_size || 0,
            type: 'file',
            ai_classification: file.ai_classification || null,
            uploaded_at: file.uploaded_at || file.created_at || null,
            folder_id: file.folder_id ?? folderId,
          }));
          const combinedRows = [...folderRows, ...fileRows];
          setRows(combinedRows);
          if (folderId) {
            const currentFolderData = folderRows.find(f => f.originalId === parseInt(folderId));
            if (currentFolderData) setCurrentFolderName(currentFolderData.name);
          }
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching folders:', error);
        setError(`Error fetching folders: ${error.message}`);
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setError(`Error fetching folders: ${error.message}`);
      setRows([]);
    }
  };

  const fetchPersonalFolderContents = async (folderId) => {
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      setRows([]);
      return;
    }
    try {
      const response = await enhancedApi.getPersonalFolderContents(folderId);
      const data = response.data || response;
      
      if (!data.folders && !data.files) {
        setRows([]);
        setError('No folders or files found in this folder');
      } else {
        const folderRows = (data.folders || []).map(folder => ({
          id: `folder-${folder.id}`,
          originalId: folder.id,
          name: folder.name || 'Unnamed Folder',
          type: 'folder',
        }));
        const fileRows = (data.files || []).map((file, index) => ({
          id: `file-${file.id || `${Date.now()}-${index}`}`,
          originalId: file.id,
          file_name: file.file_name || file.name || 'Unnamed File',
          file_url: file.file,
          file_size: file.file_size || 0,
          type: 'file',
          ai_classification: file.ai_classification || null,
          uploaded_at: file.uploaded_at || file.created_at || null,
          folder_id: file.folder_id ?? null,
        }));
        const combinedRows = [...folderRows, ...fileRows];
        setRows(combinedRows);
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching personal folder contents:', error);
      setError(`Error fetching folder contents: ${error.message}`);
      setRows([]);
    }
  };

  const fetchInternalFolderContents = async (folderId) => {
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      setRows([]);
      return;
    }
    try {
      const response = await enhancedApi.getInternalFolderContents(folderId);
      const data = response.data || response;
      
      if (!data.folders && !data.files) {
        setRows([]);
        setError('No folders or files found in this folder');
      } else {
        const folderRows = (data.folders || []).map(folder => ({
          id: `folder-${folder.id}`,
          originalId: folder.id,
          name: folder.name || 'Unnamed Folder',
          type: 'folder',
        }));
        const fileRows = (data.files || []).map((file, index) => ({
          id: `file-${file.id || `${Date.now()}-${index}`}`,
          originalId: file.id,
          file_name: file.file_name || file.name || 'Unnamed File',
          file_url: file.file,
          file_size: file.file_size || 0,
          type: 'file',
          ai_classification: file.ai_classification || null,
          uploaded_at: file.uploaded_at || file.created_at || null,
          folder_id: file.folder_id ?? null,
        }));
        const combinedRows = [...folderRows, ...fileRows];
        setRows(combinedRows);
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching internal folder contents:', error);
      setError(`Error fetching folder contents: ${error.message}`);
      setRows([]);
    }
  };

  const handleCreateFolder = async () => {
    console.log('Creating folder with:', { folderNameInput, currentDirectory, currentClient, currentFolder });
    if (!folderNameInput) {
      setError('Folder name is empty');
      return;
    }
    const userId = getUserId();
    if (!userId) {
      setError('User ID not found');
      return;
    }
    if (!['Internal files', 'My files', 'Client files'].includes(currentDirectory)) {
      setError('Invalid directory for folder creation');
      return;
    }
    try {
      try {
        let response;
        if (currentDirectory === 'Internal files') {
          // For internal folders, we need to create them using the internal folders API
          response = await enhancedApi.createInternalFolder({
            name: folderNameInput,
            parent: currentFolder ? currentFolder.originalId : null,
          });
        } else if (currentDirectory === 'My files') {
          // For personal folders, we need to create them using the personal folders API
          response = await enhancedApi.createPersonalFolder({
            name: folderNameInput,
            parent: currentFolder ? currentFolder.originalId : null,
          });
        } else if (currentDirectory === 'Client files') {
          // For client folders, we need to create them using the client folders API
          if (!currentClient?.originalId) {
            setError('No client selected for folder creation');
            return;
          }
          response = await enhancedApi.createClientFolder(currentClient.originalId, {
            name: folderNameInput,
            parent_folder: currentFolder ? currentFolder.originalId : null,
          });
        } else {
          setError('Invalid directory for folder creation');
          return;
        }
        
        if (response.status === 200 || response.status === 201) {
          const newFolder = response.data || response;
          await fetchFolders();
          setCurrentFolder({
            id: `folder-${newFolder.id}`,
            originalId: newFolder.id,
            name: newFolder.name,
            type: 'folder',
          });
          setCurrentFolderName(newFolder.name);
          const newFolderPath = {
            id: `folder-${newFolder.id}`,
            originalId: newFolder.id,
            name: newFolder.name,
            type: 'folder',
          };
          if (currentFolder) {
            setCurrentFolderPath([...currentFolderPath, newFolderPath]);
          } else {
            setCurrentFolderPath([newFolderPath]);
          }
          handleCloseFolderDialog();
          alert('Folder created successfully');
        } else {
          setError(`Failed to create folder: ${response.data?.message || response.data?.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error creating folder:', error);
        setError(`Failed to create folder: ${error.message}`);
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      setError(`Failed to create folder: ${error.message}`);
    }
  };

  const handleESignRequest = async () => {
    handleMenuClose();
    if (!selectedRow || selectedRow.type !== 'file') {
      setError('Select a file first.');
      return;
    }
    const client_id = currentClient?.originalId;
    if (!client_id) {
      setError('Open a client’s folder to send documents for e-signature.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let signer_email = '';
      let signer_name = '';
      try {
        const cr = await api.get(`/api/multi-tenant/clients/${client_id}/`);
        const c = cr.data;
        signer_email = c?.primary_contact?.email || c?.email || '';
        signer_name = c?.primary_contact?.display_name || c?.display_name || '';
      } catch (_) {
        /* optional client detail */
      }
      const fileId = selectedRow.originalId;
      const fileUrl = selectedRow.file_url;
      const pdfUrl = fileUrl?.startsWith('http') ? fileUrl : `${API_BASE_URL}${fileUrl}`;
      const response = await api.post('/api/esignatures/create-from-file/', {
        document_name: selectedRow.file_name || 'Untitled',
        signer_email,
        signer_name,
        client_id,
        source_file_id: fileId,
      });
      const docId = response.data?.id;
      const docPdfUrl = response.data?.pdf_url;
      if (docId) {
        if (filePreview) {
          handleCloseFilePreview();
        }
        setESignDocumentId(docId);
        setESignPrepareFile({
          name: selectedRow.file_name || 'Document',
          pdfUrl: docPdfUrl || pdfUrl,
        });
      } else {
        setError('Could not create e-sign document.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start e-sign');
    } finally {
      setLoading(false);
    }
  };

  const addRecentlyViewedFile = async (fileId, fileName) => {
    const userId = getUserId();
    try {
      const response = await enhancedApi.addRecentlyViewedFile({
        file_id: fileId,
        file_name: fileName,
      });
              if (response.status !== 200 && response.status !== 201) {
          console.error('Failed to add recently viewed file:', response.data?.message || 'Unknown error');
        }
    } catch (error) {
      console.error('Error adding recently viewed file:', error);
    }
  };

  const handleClientClick = (client) => {
    setCurrentClient(client);
    const clientIndex = currentClientPath.findIndex(c => c.originalId === client.originalId);
    if (clientIndex === -1) {
      setCurrentClientPath([...currentClientPath, client]);
    } else {
      setCurrentClientPath(currentClientPath.slice(0, clientIndex + 1));
    }
    setCurrentClientName(client.name);
    setCurrentFolderPath([]);
    setCurrentFolder(null);
    setCurrentFolderName('');
    setViewingFolderFiles(false);
    setViewingClients(false);
    navigate(`/files/clients/${client.originalId}`);
  };

  const getDirectoryPath = (directory, folderId) => {
    const pathMap = {
      'My files': `/files/personal/folders/${folderId}/contents`,
      'Internal files': `/files/internal/folders/${folderId}/contents`,
      'Client files': `/files/clients/folders/${folderId}/contents`,
      'Recently viewed': `/files/recent/folders/${folderId}/contents`
    };
    return pathMap[directory] || `/files/${directory.toLowerCase().replace(' ', '-')}/folders/${folderId}/contents`;
  };

  const handleFolderClick = useCallback((folder) => {
    if (!folder || !folder.originalId) {
      console.error('Invalid folder clicked:', folder);
      setError('Invalid folder selected');
      return;
    }
    setCurrentFolder(folder);
    setCurrentFolderName(folder.name);
    setViewingFolderFiles(true);
    setRows([]);
    const folderIndex = currentFolderPath.findIndex(f => f.originalId === folder.originalId);
    if (folderIndex === -1) {
      setCurrentFolderPath([...currentFolderPath, folder]);
    } else {
      setCurrentFolderPath(currentFolderPath.slice(0, folderIndex + 1));
    }
    const navigateUrl = currentClient
      ? `/files/clients/${currentClient.originalId}/folders/${folder.originalId}/contents`
      : getDirectoryPath(currentDirectory, folder.originalId);
    navigate(navigateUrl);
  }, [currentClient, currentFolderPath, navigate, currentDirectory]);

  const handleMoreIconClick = (event, row) => {
    event.stopPropagation();
    setSelectedRow(row);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenFolderDialog = () => {
    console.log('Opening folder dialog for:', { currentDirectory, currentClient, viewingClients });
    setOpenFolderDialog(true);
    setFolderNameInput('');
    setError(null);
  };

  const handleCloseFolderDialog = () => {
    setOpenFolderDialog(false);
    setFolderNameInput('');
    setError(null);
  };

  const handleCloseFilePreview = () => {
    // Clean up blob URL to prevent memory leaks
    if (fileToView && fileToView.startsWith('blob:')) {
      URL.revokeObjectURL(fileToView);
    }
    setFilePreview(false);
    setFileToView(null);
  };

  const handleOpenFilePreview = async (file) => {
    const fileId = file.originalId;
    const fileName = file.file_name || file.id;
    
    if (!file.file_url) {
      setError('Unable to open file: Invalid file URL');
      return;
    }

    try {
      await addRecentlyViewedFile(fileId, fileName);
      
      // Set the selected row for eSign request functionality
      setSelectedRow(file);

      // Determine file type from filename
      const fileType = fileName.split('.').pop().toLowerCase();
      setFileType(fileType);
      
      // For PDF files, we need to fetch the file and create a blob URL for the PDF viewer
      if (fileType === 'pdf') {
        try {
          console.log('Fetching PDF from URL:', file.file_url);
          
          // Fetch the file as blob
          const response = await fetch(file.file_url, {
            headers: getAuthHeaders(),
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          
          setFileToView(blobUrl);
          setFilePreview(true);
          
        } catch (error) {
          console.error('Error fetching PDF file:', error);
          setError(`Failed to load PDF: ${error.message}`);
        }
      } else {
        // For non-PDF files, use the original URL
        setFileToView(file.file_url);
        setFilePreview(true);
      }
      
    } catch (error) {
      console.error('Error opening file preview:', error);
      setError(`Failed to open file: ${error.message}`);
    }
  };

  const handleRename = () => {
    if (selectedRow) {
      setNewName(selectedRow.type === 'file' ? selectedRow.file_name : selectedRow.name);
      setOpenRenameDialog(true);
    } else {
      setError('No item selected for renaming');
    }
    handleMenuClose();
  };

  const handleSaveRename = async () => {
    if (!newName) {
      setError('New name cannot be empty');
      return;
    }
    try {
      if (selectedRow.type === 'folder') {
        const folderId = selectedRow.originalId || selectedRow.id.split('-')[1];
        const body = { name: newName, client: currentClient.originalId, user: getUserId(), parent_folder: currentFolder ? currentFolder.originalId : null, folder_id: folderId };
        const response = await enhancedApi.updateFolder(currentClient.originalId, folderId, body);
        if (response.status === 200 || response.status === 201) {
          const refreshFolderId = currentFolder ? currentFolder.originalId : null;
          await fetchClientFiles(currentClient.originalId, refreshFolderId, showArchived);
          alert('Renamed successfully');
        } else {
          setError('Failed to rename: ' + (response.data?.error || response.data?.message || 'Unknown error'));
        }
      } else {
        const fileId = selectedRow.originalId || selectedRow.id.split('-')[1];
        const body = { file_name: newName };
        
        // Use the new rename API endpoint
        const response = await enhancedApi.renameFile(currentClient.originalId, fileId, body);
        if (response.status === 200 || response.status === 201) {
          const refreshFolderId = currentFolder ? currentFolder.originalId : null;
          await fetchClientFiles(currentClient.originalId, refreshFolderId, showArchived);
          alert('Renamed successfully');
        } else {
          setError('Failed to rename: ' + (response.data?.error || response.data?.message || 'Unknown error'));
        }
      }
    } catch (error) {
      setError('Failed to rename: ' + error.message);
    } finally {
      setOpenRenameDialog(false);
      setNewName('');
    }
  };

  const handleCloseRenameDialog = () => {
    setOpenRenameDialog(false);
    setNewName('');
    setError(null);
  };

  const handleDownload = () => {
    if (selectedRow.type === 'file' && selectedRow.file_url) {
      window.open(selectedRow.file_url, '_blank');
    }
    handleMenuClose();
  };

  const fetchMoveClients = async () => {
    try {
      const response = await enhancedApi.getClients();
      const data = response.data || response;
      setMoveClients(data.map(client => ({ id: client.id, name: client.display_name || client.name || 'Unnamed Client' })));
    }
    catch (error) {
      console.error('Error fetching move clients:', error);
      setError(`Error fetching clients: ${error.message}`);
      setMoveClients([]);
    }
  };

  const fetchMoveFolders = async (clientId) => {
    try {
      const response = await enhancedApi.getClientFolders(clientId);
      const data = response.data || response;
      setMoveFolders((data || []).map(folder => ({ id: folder.id, name: folder.name || 'Unnamed Folder', clientId: clientId })));
    } catch (error) {
      console.error('Error fetching move folders:', error);
      setError(`Error fetching folders: ${error.message}`);
      setMoveFolders([]);
    }
  };

  const handleMove = async () => {
    handleMenuClose();
    setError(null);
    setOpenMoveDialog(true);
    await fetchMoveClients();
  };

  const handleMoveClientClick = async (clientId) => {
    setSelectedMoveClient(clientId);
    setSelectedMoveFolder(undefined); // undefined = not yet selected (null = root, number = folder)
    await fetchMoveFolders(clientId);
  };

  const handleMoveFolderSelect = (folderId) => {
    setSelectedMoveFolder(folderId);
  };

  const handleMoveConfirm = async () => {
    if (!selectedMoveClient || !selectedRow?.originalId) {
      setError('Please select a client and ensure a file is selected');
      return;
    }
    const fileId = selectedRow.originalId;
    if (selectedRow.type !== 'file') {
      setError('Only files can be moved.');
      return;
    }
    try {
      setError(null);
      const response = await enhancedApi.moveFile({
        fileId: fileId,
        destinationFolderId: selectedMoveFolder === null ? null : selectedMoveFolder,
        clientId: selectedMoveClient,
      });
      if (response.status === 200 || response.status === 201) {
        setOpenMoveDialog(false);
        setSelectedMoveClient(null);
        setSelectedMoveFolder(null);
        setMoveFolders([]);
        setSelectedRow(null);
        handleMenuClose();
        // Refresh the SOURCE view so the moved file disappears from current folder
        const srcClientId = currentClient?.originalId || (clientId ? parseInt(clientId, 10) : null);
        if (currentDirectory === 'Client files' && srcClientId) {
          await fetchClientFiles(srcClientId, currentFolder?.originalId ?? null, showArchived);
        } else if (currentDirectory === 'Internal files') {
          await fetchFolders();
        } else if (currentDirectory === 'My files') {
          await fetchFolders();
        } else if (srcClientId) {
          await fetchClientFiles(srcClientId, currentFolder?.originalId ?? null, showArchived);
        }
        alert('File moved successfully');
      } else {
        setError(response.data?.error || response.data?.message || 'Failed to move file');
      }
    } catch (error) {
      console.error('Error moving file:', error);
      const msg = error.response?.data?.error || error.message;
      setError(msg || 'Error moving file');
    }
  };

  const handleCloseMoveDialog = () => {
    setOpenMoveDialog(false);
    setSelectedMoveClient(null);
    setSelectedMoveFolder(null);
    setMoveFolders([]);
    setError(null);
  };

  const handleFolderAccess = () => {
    handleMenuClose();
  };

  const handleEditFile = () => {
    handleMenuClose();
  };

  const handlePreview = () => {
    if (selectedRow.type === 'file') handleOpenFilePreview(selectedRow);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    setViewDetailsDialogOpen(true);
    handleMenuClose();
  };

  const handleShareFile = () => {
    setEmailRecipients('');
    setIncludeMessage(false);
    setPersonalizedMessage('');
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleShareFileSubmit = async () => {
    if (!selectedRow || !emailRecipients?.trim()) {
      setError('Please enter at least one email recipient');
      return;
    }
    const effectiveClientId = currentClient?.originalId || (currentDirectory === 'Client files' && clientId ? parseInt(clientId, 10) : null);
    if (!effectiveClientId) {
      setError('No client selected. Share is only available for client files.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fileId = selectedRow.originalId || selectedRow.id?.replace('file-', '');
      await api.post(
        `/api/multi-tenant/clients/${effectiveClientId}/files/${fileId}/share/`,
        {
          email_recipients: emailRecipients.split(',').map((e) => e.trim()).filter(Boolean),
          message: includeMessage ? personalizedMessage : null,
        }
      );
      setShareDialogOpen(false);
      setEmailRecipients('');
      setIncludeMessage(false);
      setPersonalizedMessage('');
      alert('File shared successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || 'Failed to share file');
    } finally {
      setLoading(false);
    }
  };

  const handleSendFileLink = () => {
    setEmailRecipients('');
    setIncludeMessage(false);
    setPersonalizedMessage('');
    setSendFileLinkDialogOpen(true);
    handleMenuClose();
  };

  const handleSendFileLinkSubmit = async () => {
    if (!selectedRow || !emailRecipients?.trim()) {
      setError('Please enter at least one email recipient');
      return;
    }
    const effectiveClientId = currentClient?.originalId || (currentDirectory === 'Client files' && clientId ? parseInt(clientId, 10) : null);
    if (!effectiveClientId) {
      setError('No client selected. Send file link is only available for client files.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fileId = selectedRow.originalId || selectedRow.id?.replace('file-', '');
      await api.post(
        `/api/multi-tenant/clients/${effectiveClientId}/files/${fileId}/send-file-link/`,
        {
          email_recipients: emailRecipients.split(',').map((e) => e.trim()).filter(Boolean),
          message: includeMessage ? personalizedMessage : null,
        }
      );
      setSendFileLinkDialogOpen(false);
      setEmailRecipients('');
      setIncludeMessage(false);
      setPersonalizedMessage('');
      alert('File link sent successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || 'Failed to send file link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPortalLink = async () => {
    const portalBase = process.env.REACT_APP_CLIENT_PORTAL_URL || 'http://localhost:3000';
    const portalUrl = `${portalBase}/files`;
    try {
      await navigator.clipboard.writeText(portalUrl);
      alert('Portal link copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
    handleMenuClose();
  };

  const handleArchive = () => {
    setArchiveConfirmOpen(true);
    handleMenuClose();
  };

  const handleArchiveConfirm = async () => {
    const effectiveClientId = currentClient?.originalId || (currentDirectory === 'Client files' && clientId ? parseInt(clientId, 10) : null);
    if (!selectedRow?.originalId || !effectiveClientId) {
      setError('Cannot archive: no file or client selected');
      setArchiveConfirmOpen(false);
      return;
    }
    if (currentDirectory !== 'Client files') {
      setError('Archive is only available for client files.');
      setArchiveConfirmOpen(false);
      return;
    }
    const isUnarchive = showArchived;
    setLoading(true);
    setError(null);
    try {
      if (selectedRow.type === 'folder') {
        await enhancedApi.updateFolder(effectiveClientId, selectedRow.originalId, { is_archived: !isUnarchive });
      } else {
        const fileId = String(selectedRow.originalId);
        await api.patch(
          `/api/multi-tenant/clients/${effectiveClientId}/files/${fileId}/`,
          { is_archived: !isUnarchive }
        );
      }
      setArchiveConfirmOpen(false);
      await fetchClientFiles(effectiveClientId, currentFolder?.originalId ?? null, showArchived);
      alert(isUnarchive ? 'Restored to active files successfully!' : 'Archived successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || (isUnarchive ? 'Failed to restore' : 'Failed to archive'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const handleAddFile = () => {
    fileInputRef.current.click();
    handleAddMenuClose();
  };

  const handleAddFolder = () => {
    handleOpenFolderDialog();
    handleAddMenuClose();
  };

  const handleAuditLog = async (row) => {
    setAuditLogOpen(true);
    setAuditLogLoading(true);
    setAuditLogError(null);
    setAuditLogData([]);
    try {
      if (row.type === 'folder') {
        setAuditLogData([]);
        setAuditLogError('Audit log for folders is not yet implemented.');
        setAuditLogLoading(false);
        return;
      }
      const response = await api.get(`/api/multi-tenant/files/${row.originalId}/audit-log/`);
      const data = response.data;
      setAuditLogData(data.audit_log || []);
    } catch (err) {
      setAuditLogError(err.message);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const clientColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FolderIcon style={{ marginRight: '8px' }} />
          {params.row.name}
        </div>
      ),
    },
  ];

  const ESIGN_CHIP_CONFIG = {
    draft:              { label: 'E-Sign Draft',       color: 'default' },
    sent_for_signature: { label: 'Awaiting Signature', color: 'info'    },
    in_progress:        { label: 'Awaiting Signature', color: 'info'    },
    completed:          { label: 'Signed',             color: 'success' },
    declined:           { label: 'Declined',           color: 'error'   },
    expired:            { label: 'Expired',            color: 'error'   },
    cancelled:          { label: 'Cancelled',          color: 'default' },
  };

  const renderAIClassificationChip = (row) => {
    const ac = row.ai_classification;
    if (!ac) return null;
    const docType = ac.document_type || 'Other';
    const taxYear = ac.tax_year || '';
    const conf = ac.confidence != null ? Math.round(ac.confidence * 100) : null;
    const label = [docType, taxYear, conf != null ? `${conf}%` : ''].filter(Boolean).join(' · ');
    const chipColor = conf >= 70 ? 'success' : conf >= 50 ? 'warning' : 'default';
    return (
      <Chip label={label} color={chipColor} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
    );
  };

  const renderClassificationPending = (row) => {
    if (row.type !== 'file' || row.ai_classification != null) return null;
    const uploadedAt = row.uploaded_at;
    if (!uploadedAt) return null;
    const minsAgo = (Date.now() - new Date(uploadedAt).getTime()) / (1000 * 60);
    if (minsAgo >= 5) return null;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, gap: 0.5 }}>
        <CircularProgress size={12} sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Classifying...</Typography>
      </Box>
    );
  };

  const renderMoveToSuggestedButton = (row) => {
    if (currentDirectory !== 'Client files' || !currentClient) return null;
    const ac = row.ai_classification;
    const suggested = ac?.suggested_folder;
    if (!suggested?.id) return null;
    const fileFolderId = row.folder_id ?? currentFolder?.originalId;
    const destId = typeof suggested.id === 'number' ? suggested.id : parseInt(suggested.id, 10);
    if (fileFolderId != null && (fileFolderId === destId || String(fileFolderId) === String(destId))) return null;
    const rawId = row.originalId;
    if (!rawId || String(rawId).startsWith('dr_')) return null;
    const isMoving = movingFileId === row.id;
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={isMoving}
        onClick={async (ev) => {
          ev.stopPropagation();
          setMovingFileId(row.id);
          setError(null);
          try {
            await enhancedApi.moveFileToFolderById(rawId, destId);
            await fetchClientFiles(currentClient.originalId, currentFolder?.originalId ?? null, showArchived);
          } catch (err) {
            setError(err?.response?.data?.error || 'Failed to move file');
            console.error(err);
          } finally {
            setMovingFileId(null);
          }
        }}
        sx={{ ml: 1, textTransform: 'none', minWidth: 0, py: 0.25 }}
      >
        {isMoving ? <CircularProgress size={14} /> : 'Move to suggested'}
      </Button>
    );
  };

  const fileColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => {
        // originalId is the raw database file ID; only files (not folders) get esign chips
        const esignInfo = params.row.type === 'file' && params.row.originalId
          ? eSignStatuses[String(params.row.originalId)]
          : null;
        const chipCfg = esignInfo ? (ESIGN_CHIP_CONFIG[esignInfo.status] || { label: esignInfo.status_display || esignInfo.status, color: 'default' }) : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {params.row.type === 'folder'
              ? <FolderIcon style={{ marginRight: '8px' }} />
              : <FileIcon style={{ marginRight: '8px' }} />}
            {params.row.type === 'folder' ? params.row.name : (params.row.file_name || 'Unnamed File')}
            {params.row.type === 'file' && renderAIClassificationChip(params.row)}
            {params.row.type === 'file' && renderClassificationPending(params.row)}
            {params.row.type === 'file' && renderMoveToSuggestedButton(params.row)}
            {chipCfg && (
              <Chip
                label={chipCfg.label}
                color={chipCfg.color}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </div>
        );
      },
    },
    {
      field: 'file_size',
      headerName: 'Size (bytes)',
      flex: 1,
      renderCell: (params) => (
        <div>{params.row.type === 'folder' ? '-' : params.row.file_size || 0}</div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div onClick={(e) => { e.stopPropagation(); handleMoreIconClick(e, params.row); }}>
          <IconButton size="small">
            <MoreVertIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const ClientListItem = ({ clientName, icon, isActive, onClick }) => (
    <ListItem
      button
      onClick={onClick}
      style={{ backgroundColor: isActive ? '#f0f0f0' : 'transparent', cursor: 'pointer' }}
    >
      <ListItemIcon sx={{ minWidth: '25px' }}>{icon}</ListItemIcon>
      <ListItemText primary={<Typography variant="body2">{clientName}</Typography>} />
    </ListItem>
  );

  const hasAnyClassifiedFile = rows.some((row) => row.type === 'file' && row.ai_classification != null);

  // Filtered rows for search and document type
  let filteredRows = rows;
  if (documentTypeFilter !== 'all') {
    filteredRows = filteredRows.filter((row) =>
      row.type !== 'file' || row.ai_classification?.document_type === documentTypeFilter
    );
  }
  if (searchTerm) {
    filteredRows = filteredRows.filter((row) =>
      (row.name || row.file_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  if (eSignPrepareFile && eSignDocumentId) {
    return (
      <PageLayout onOptionSelect={handleOptionSelect} sx={{ p: 0 }} hideGlobalSearch>
        <ESignaturePrepare
          documentId={eSignDocumentId}
          documentName={eSignPrepareFile.name}
          pdfUrl={eSignPrepareFile.pdfUrl}
          onBack={() => {
            setESignPrepareFile(null);
            setESignDocumentId(null);
          }}
          onSent={() => {
            setESignPrepareFile(null);
            setESignDocumentId(null);
            if (clientId) {
              api
                .get(`/api/multi-tenant/clients/${clientId}/esignature-file-statuses/`)
                .then((res) => setESignStatuses(res.data || {}))
                .catch(() => {});
            }
          }}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout onOptionSelect={handleOptionSelect} sx={{ p: 0 }} hideGlobalSearch>
        <Grid container spacing={{ xs: 2 }} sx={{ width: '100%', minHeight: 0, flex: 1 }}>
      <Grid size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 2 }}>
        <Paper sx={{ height: 'auto', width: 'auto', p: 1.25 }}>
          <Typography component="h3" variant="h6" sx={{ pl: 2 }}>{currentDirectory}</Typography>
          <List>
            <ClientListItem
              clientName="Client files"
              icon={<FolderIcon />}
              isActive={currentDirectory === 'Client files'}
              onClick={() => {
                navigate('/files/clients/root');
                setCurrentClientPath([]);
                setCurrentFolderPath([]);
                setCurrentFolder(null);
                setViewingClients(true);
                setViewingFolderFiles(false);
                setRows([]);
                setCurrentDirectory('Client files');
                fetchClients();
              }}
            />
            <ClientListItem
              clientName="Internal files"
              icon={<FolderIcon />}
              isActive={currentDirectory === 'Internal files'}
              onClick={() => {
                navigate('/files/internal/root');
                setCurrentClientPath([]);
                setCurrentFolderPath([]);
                setCurrentFolder(null);
                setViewingFolderFiles(false);
                setRows([]);
                setCurrentDirectory('Internal files');
                fetchFolders();
              }}
            />
            <ClientListItem
              clientName="My files"
              icon={<PersonIcon />}
              isActive={currentDirectory === 'My files'}
              onClick={() => {
                navigate('/files/personal/root');
                setCurrentClientPath([]);
                setCurrentFolderPath([]);
                setCurrentFolder(null);
                setCurrentClient(null);
                setViewingFolderFiles(false);
                setRows([]);
                setCurrentDirectory('My files');
                fetchFolders();
              }}
            />
            <ClientListItem
              clientName="Recently viewed"
              icon={<AccessTimeIcon />}
              isActive={currentDirectory === 'Recently viewed'}
              onClick={() => {
                navigate('/files/recent/root');
                setCurrentClientPath([]);
                setCurrentFolderPath([]);
                setCurrentFolder(null);
                setViewingFolderFiles(false);
                setRows([]);
                setCurrentDirectory('Recently viewed');
              }}
            />
          </List>
        </Paper>

        {currentDirectory === 'My files' && (
          <Paper sx={{ height: 'auto', p: 1.25, mt: 2 }}>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid><Typography component="h3" variant="h6" sx={{ pl: 2 }}>My Files</Typography></Grid>
              <Grid display={'flex'}>
                <Tooltip title="Upload new file">
                  <IconButton color="primary" component="label">
                    <CreateFileIcon />
                    <input type="file" hidden multiple onChange={handleFileUpload} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create new folder">
                  <IconButton color="primary" onClick={handleOpenFolderDialog}>
                    <CreateNewFolderIcon />
                  </IconButton>
                </Tooltip>
                <Dialog open={openFolderDialog} onClose={handleCloseFolderDialog}>
                  <DialogTitle>Create New Folder</DialogTitle>
                  <DialogContent>
                    <DialogContentText>Enter the name of the new folder.</DialogContentText>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Folder Name"
                      type="text"
                      fullWidth
                      sx={{ marginTop: '2rem' }}
                      value={folderNameInput}
                      onChange={(e) => setFolderNameInput(e.target.value)}
                    />
                    {error && <Typography color="error">{error}</Typography>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseFolderDialog} color="secondary">Cancel</Button>
                    <Button onClick={handleCreateFolder} variant="contained" color="primary">Create</Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Grid>
          </Paper>
        )}

        {currentDirectory === 'Internal files' && (
          <Paper sx={{ height: 'auto', p: 1.25, mt: 2 }}>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid><Typography component="h3" variant="h6" sx={{ pl: 2 }}>Internal Files</Typography></Grid>
              <Grid display={'flex'}>
                <Tooltip title="Upload new file">
                  <IconButton color="primary" component="label">
                    <CreateFileIcon />
                    <input type="file" hidden multiple onChange={handleFileUpload} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create new folder">
                  <IconButton color="primary" onClick={handleOpenFolderDialog}>
                    <CreateNewFolderIcon />
                  </IconButton>
                </Tooltip>
                <Dialog open={openFolderDialog} onClose={handleCloseFolderDialog}>
                  <DialogTitle>Create New Folder</DialogTitle>
                  <DialogContent>
                    <DialogContentText>Enter the name of the new folder.</DialogContentText>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Folder Name"
                      type="text"
                      fullWidth
                      sx={{ marginTop: '2rem' }}
                      value={folderNameInput}
                      onChange={(e) => setFolderNameInput(e.target.value)}
                    />
                    {error && <Typography color="error">{error}</Typography>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseFolderDialog} color="secondary">Cancel</Button>
                    <Button onClick={handleCreateFolder} variant="contained" color="primary">Create</Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Grid>
          </Paper>
        )}

        {!viewingClients && currentClient && currentDirectory === 'Client files' && (
          <Paper sx={{ height: 'auto', p: 1.25, mt: 2 }}>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid><Typography component="h3" variant="h6" sx={{ pl: 2 }}>{viewingFolderFiles ? currentFolderName : currentClientName}</Typography></Grid>
              <Grid display={'flex'} alignItems="center" gap={1}>
                <FormControlLabel
                  control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
                  label={<Typography variant="body2">View archived</Typography>}
                />
                <Tooltip title="Upload new file">
                  <IconButton color="primary" component="label">
                    <CreateFileIcon />
                    <input type="file" hidden multiple onChange={handleFileUpload} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create new subfolder">
                  <IconButton color="primary" onClick={handleOpenFolderDialog}>
                    <CreateNewFolderIcon />
                  </IconButton>
                </Tooltip>
                <Dialog open={openFolderDialog} onClose={handleCloseFolderDialog}>
                  <DialogTitle>Create New Subfolder</DialogTitle>
                  <DialogContent>
                    <DialogContentText>Enter the name of the new folder under {viewingFolderFiles ? currentFolderName : currentClientName}.</DialogContentText>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Folder Name"
                      type="text"
                      fullWidth
                      sx={{ marginTop: '2rem' }}
                      value={folderNameInput}
                      onChange={(e) => setFolderNameInput(e.target.value)}
                    />
                    {error && <Typography color="error">{error}</Typography>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseFolderDialog} color="secondary">Cancel</Button>
                    <Button onClick={handleCreateFolder} variant="contained" color="primary">Create</Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {selectedRow && selectedRow.type === 'folder' && (
            <>
              <MenuItem onClick={handleRename}>Rename</MenuItem>
              <MenuItem onClick={handleDownload}>Download</MenuItem>
              <MenuItem onClick={handleMove}>Move</MenuItem>
              <MenuItem onClick={handleFolderAccess}>Folder Access</MenuItem>
              <MenuItem onClick={handleArchive}>{showArchived ? 'Unarchive' : 'Archive'}</MenuItem>
              <MenuItem onClick={() => handleAuditLog(selectedRow)}>History</MenuItem>
            </>
          )}
          {selectedRow && selectedRow.type === 'file' && (
            <>
              <MenuItem onClick={handleRename}>Rename</MenuItem>
              <MenuItem onClick={handleDownload}>Download</MenuItem>
              <MenuItem onClick={handleMove}>Move</MenuItem>
              <MenuItem onClick={handleEditFile}>Edit file</MenuItem>
              <MenuItem onClick={handlePreview}>Preview</MenuItem>
              <MenuItem onClick={handleViewDetails}>View details</MenuItem>
              <MenuItem onClick={handleESignRequest}>Request E-sign</MenuItem>
              <MenuItem onClick={handleShareFile}>Share file</MenuItem>
              <MenuItem onClick={handleSendFileLink}>Send file link</MenuItem>
              <MenuItem onClick={handleCopyPortalLink}>Copy portal link</MenuItem>
              <MenuItem onClick={handleArchive} disabled={currentDirectory !== 'Client files'}>{showArchived ? 'Unarchive' : 'Archive'}</MenuItem>
              <MenuItem onClick={() => handleAuditLog(selectedRow)}>History</MenuItem>
            </>
          )}
        </Menu>

        <Dialog open={openRenameDialog} onClose={handleCloseRenameDialog}>
          <DialogTitle>Rename</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the new name for the {selectedRow?.type === 'file' ? 'file' : 'folder'}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="New name"
              type="text"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {error && <Typography color="error">{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRenameDialog} color="secondary">Cancel</Button>
            <Button onClick={handleSaveRename} variant="contained" color="primary">Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openMoveDialog} onClose={handleCloseMoveDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Move File: {selectedRow?.file_name || 'Selected File'} from {currentDirectory}</DialogTitle>
          <DialogContent>
            {!selectedMoveClient ? (
              <>
                <DialogContentText>Select a client to move the file to:</DialogContentText>
                {moveClients.map(client => (
                  <ListItem button key={client.id} onClick={() => handleMoveClientClick(client.id)}>
                    <ListItemIcon><FolderIcon /></ListItemIcon>
                    <ListItemText primary={client.name} />
                  </ListItem>
                ))}
              </>
            ) : (
              <>
                <DialogContentText>Select a folder under {moveClients.find(c => c.id === selectedMoveClient)?.name} or move to main client:</DialogContentText>
                <ListItem button onClick={() => handleMoveFolderSelect(null)}>
                  <ListItemIcon><FolderIcon /></ListItemIcon>
                  <ListItemText primary="Move to Main Client (root)" />
                </ListItem>
                {moveFolders.map(folder => (
                  <ListItem button key={folder.id} onClick={() => handleMoveFolderSelect(folder.id)}>
                    <ListItemIcon><FolderIcon /></ListItemIcon>
                    <ListItemText primary={folder.name} />
                  </ListItem>
                ))}
              </>
            )}
            {error && <Typography color="error">{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMoveDialog} color="secondary">Cancel</Button>
            {selectedMoveClient && selectedMoveFolder !== undefined && (
              <Button onClick={handleMoveConfirm} variant="contained" color="primary">Move Here</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={viewDetailsDialogOpen} onClose={() => setViewDetailsDialogOpen(false)}>
          <DialogTitle>File Details</DialogTitle>
          <DialogContent>
            {selectedRow && selectedRow.type === 'file' && (
              <Box sx={{ minWidth: 320 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                  {selectedRow.file_name || 'Unnamed File'}
                </Typography>
                <Typography variant="body2"><strong>Size:</strong> {selectedRow.file_size ? `${(selectedRow.file_size / 1024).toFixed(2)} KB` : 'N/A'}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {selectedRow.file_name ? selectedRow.file_name.split('.').pop()?.toUpperCase() || 'File' : 'N/A'}</Typography>
                {selectedRow.file_url && (
                  <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                    <strong>URL:</strong> {selectedRow.file_url}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={shareDialogOpen} onClose={() => { setShareDialogOpen(false); setError(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Share File</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box component="span" sx={{ color: 'primary.main', mr: 1, display: 'inline-flex', alignItems: 'center' }}>
                  <FileIcon />
                </Box>
                <Typography variant="body2">{selectedRow.file_name || 'Unnamed File'}</Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Add email recipients (comma-separated)"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch checked={includeMessage} onChange={(e) => setIncludeMessage(e.target.checked)} />}
              label="Include personalized message"
            />
            {includeMessage && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message"
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                sx={{ mt: 2, mb: 2 }}
              />
            )}
            {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShareDialogOpen(false); setError(null); }}>Cancel</Button>
            <Button onClick={handleShareFileSubmit} variant="contained" color="primary" disabled={loading}>
              {loading ? 'Sharing...' : 'Share'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={sendFileLinkDialogOpen} onClose={() => { setSendFileLinkDialogOpen(false); setError(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Send File Link</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box component="span" sx={{ color: 'primary.main', mr: 1, display: 'inline-flex', alignItems: 'center' }}>
                  <FileIcon />
                </Box>
                <Typography variant="body2">{selectedRow.file_name || 'Unnamed File'}</Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Add email recipients (comma-separated)"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch checked={includeMessage} onChange={(e) => setIncludeMessage(e.target.checked)} />}
              label="Include personalized message"
            />
            {includeMessage && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message"
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                sx={{ mt: 2, mb: 2 }}
              />
            )}
            {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSendFileLinkDialogOpen(false); setError(null); }}>Cancel</Button>
            <Button onClick={handleSendFileLinkSubmit} variant="contained" color="primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Link'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={archiveConfirmOpen} onClose={() => setArchiveConfirmOpen(false)}>
          <DialogTitle>{showArchived ? 'Restore to Active' : 'Archive'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {showArchived
                ? `Restore "${selectedRow?.file_name || selectedRow?.name || 'this item'}" to your active workspace?`
                : `Archive "${selectedRow?.file_name || selectedRow?.name || 'this item'}"? It will be moved to long-term storage and hidden from the main view. You can view and restore archived items anytime.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleArchiveConfirm} variant="contained" color="primary" disabled={loading}>
              {loading ? (showArchived ? 'Restoring...' : 'Archiving...') : (showArchived ? 'Restore' : 'Archive')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>

      <Grid size={{ xs: 12, sm: 8, md: 9, lg: 9, xl: 10 }}>
        <Paper sx={{ height: '100%', p: 2 }}>
          {/* Search Bar and Document Type Filter */}
          <Box mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search your files"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ color: 'text.secondary', mr: 1, display: 'inline-flex', alignItems: 'center', fontSize: 22 }}>
                    <SearchIcon />
                  </Box>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  fontSize: 15,
                },
              }}
            />
            {!viewingClients && hasAnyClassifiedFile && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Document type</InputLabel>
                <Select
                  value={documentTypeFilter}
                  label="Document type"
                  onChange={(e) => setDocumentTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="W-2">W-2</MenuItem>
                  <MenuItem value="1099">1099</MenuItem>
                  <MenuItem value="Bank Statement">Bank Statement</MenuItem>
                  <MenuItem value="Invoice">Invoice</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
          <Breadcrumbs separator={<NavigateNextIcon style={{ fontSize: '1.8rem' }} />} aria-label="breadcrumb" sx={{ mb: 1 }}>
            {currentDirectory === 'Client files' && (
              <Link
                onClick={() => {
                  navigate("/files/clients/root");
                  setCurrentClientPath([]);
                  setCurrentFolderPath([]);
                  setViewingClients(true);
                  setViewingFolderFiles(false);
                  setRows([]);
                  fetchClients();
                }}
                underline="hover"
                color="inherit"
              >
                Client files
              </Link>
            )}
            {currentDirectory === 'Internal files' && (
              <Link
                onClick={() => {
                  navigate("/files/internal/root");
                  setCurrentClientPath([]);
                  setCurrentFolderPath([]);
                  setCurrentFolder(null);
                  setViewingFolderFiles(false);
                  setRows([]);
                  fetchFolders();
                }}
                underline="hover"
                color="inherit"
              >
                Internal files
              </Link>
            )}
            {currentDirectory === 'My files' && (
              <Link
                onClick={() => {
                  navigate("/files/personal/root");
                  setCurrentClientPath([]);
                  setCurrentFolderPath([]);
                  setCurrentFolder(null);
                  setCurrentClient(null);
                  setViewingFolderFiles(false);
                  setRows([]);
                  fetchFolders();
                }}
                underline="hover"
                color="inherit"
              >
                My files
              </Link>
            )}
            {currentDirectory === 'Recently viewed' && (
              <Link
                onClick={() => {
                  navigate("/files/recent/root");
                  setCurrentClientPath([]);
                  setCurrentFolderPath([]);
                  setCurrentFolder(null);
                  setViewingFolderFiles(false);
                  setRows([]);
                }}
                underline="hover"
                color="inherit"
              >
                Recently viewed
              </Link>
            )}
            {currentClientPath.map((client, index) => (
              <Link
                key={client.originalId}
                color={index === currentClientPath.length - 1 && currentFolderPath.length === 0 ? "textPrimary" : "inherit"}
                onClick={() => { navigate(`/files/clients/${client.originalId}`); handleClientClick(client); }}
                underline="hover"
                style={{ cursor: 'pointer' }}
              >
                {client.name}
              </Link>
            ))}
            {currentFolderPath.map((folder, index) => (
              <Link
                key={folder.originalId}
                color={index === currentFolderPath.length - 1 ? "textPrimary" : "inherit"}
                onClick={() => {
                  const newPath = currentFolderPath.slice(0, index + 1);
                  setCurrentFolderPath(newPath);
                  setCurrentFolder(folder);
                  setCurrentFolderName(folder.name);
                  setViewingFolderFiles(true);
                  setRows([]);
                  const navigateUrl = currentClient
                    ? `/files/clients/${currentClient.originalId}/folders/${folder.originalId}/contents`
                    : getDirectoryPath(currentDirectory, folder.originalId);
                  navigate(navigateUrl);
                }}
                underline="hover"
                style={{ cursor: 'pointer' }}
              >
                {folder.name}
              </Link>
            ))}
          </Breadcrumbs>

          <Grid style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <Grid size={{ xs: 8 }}>
              <Typography variant="h6">
                {viewingClients
                  ? 'Clients'
                  : viewingFolderFiles
                  ? `Files in: ${currentFolderName || 'Folder'}`
                  : currentDirectory === 'My files'
                  ? 'My Files'
                  : `Files for: ${currentClientName || 'Client'}`}
              </Typography>
            </Grid>
            <Grid size={{ xs: 4 }} style={{ display: 'flex', justifyContent: 'right', alignItems: 'center', marginBottom: '0.5rem' }}>
              {viewingClients && (
                <Button variant="contained" color="primary" onClick={handleOpenFolderDialog}>
                  Add Folder
                </Button>
              )}
              {(currentDirectory === 'Internal files' || currentDirectory === 'My files' || (currentDirectory === 'Client files' && currentClient)) && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddClick}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                  <Menu
                    anchorEl={addMenuAnchorEl}
                    open={Boolean(addMenuAnchorEl)}
                    onClose={handleAddMenuClose}
                  >
                    <MenuItem onClick={handleAddFile}>
                      <CreateFileIcon style={{ marginRight: '8px' }} />
                      Add File
                    </MenuItem>
                    <MenuItem onClick={handleAddFolder}>
                      <CreateNewFolderIcon style={{ marginRight: '8px' }} />
                      Create New Folder
                    </MenuItem>
                  </Menu>
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                  />
                </>
              )}
            </Grid>
          </Grid>
          {loading ? (
            <Typography variant="body1">Loading...</Typography>
          ) : error ? (
            <Box>
              <Typography variant="body1" color="error">{error}</Typography>
              <Button onClick={() => fetchData()} variant="outlined" sx={{ mt: 1 }}>Retry</Button>
            </Box>
          ) : viewingClients ? (
            rows.length > 0 ? (
              <DataGrid
                rows={filteredRows}
                columns={clientColumns}
                autoHeight
                autoPageSize
                checkboxSelection
                disableRowSelectionOnClick
                disableSelectionOnClick
                sx={{ cursor: 'pointer' }}
                getRowId={(row) => row.id}
                onRowClick={(params) => viewingClients && handleClientClick(params.row)}
              />
            ) : (
              <Typography variant="body1">No clients found.</Typography>
            )
          ) : (
            rows.length > 0 ? (
              <DataGrid
                rows={filteredRows}
                columns={fileColumns}
                autoHeight
                checkboxSelection
                disableRowSelectionOnClick
                autoPageSize
                disableSelectionOnClick
                sx={{ cursor: 'pointer' }}
                getRowId={(row) => row.id}
                onRowClick={(params) => {
                  if (params.row.type === 'folder') handleFolderClick(params.row);
                  else handleOpenFilePreview(params.row);
                }}
              />
            ) : (
              <Typography variant="body1">
                {viewingFolderFiles ? 'No files or folders found in this folder.' : 'No files or folders found.'}
              </Typography>
            )
          )}
        </Paper>
      </Grid>

      {fileToView && (
        <Dialog open={filePreview} onClose={handleCloseFilePreview} maxWidth="lg" fullWidth>
          <DialogTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>File Viewer</div>
              <IconButton onClick={handleCloseFilePreview}>
                <FaTimes />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent style={{ position: 'relative' }}>
            {fileType === 'pdf' && currentClient?.originalId && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<BorderColorOutlinedIcon />}
                  onClick={handleESignRequest}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Select signature coordinates
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.4 }}>
                  Opens placement for Signature 1, Signature 2, … then send for signing (client folder only).
                </Typography>
              </Box>
            )}
            {fileType === 'pdf' ? (
              <Worker workerUrl={pdfWorker}>
                <div style={{ width: '100%', height: 'auto', maxHeight: '80vh', position: 'relative' }}>
                  <Viewer fileUrl={fileToView} plugins={[updatedDefaultLayoutPluginInstance]} />
                </div>
              </Worker>
            ) : (
              <DocViewer documents={[{ uri: fileToView, fileType: fileType }]} pluginRenderers={DocViewerRenderers} style={{ height: '80vh' }} />
            )}
          </DialogContent>
        </Dialog>
      )}

      {auditLogOpen && (
        <Dialog open={auditLogOpen} onClose={() => setAuditLogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Audit Log</DialogTitle>
          <DialogContent>
            {auditLogLoading ? (
              <Typography variant="body1">Loading...</Typography>
            ) : auditLogError ? (
              <Typography color="error">{auditLogError}</Typography>
            ) : (
              <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                {auditLogData.map((log, index) => (
                  <div key={index}>{log}</div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAuditLogOpen(false)} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
      )}

        </Grid>
    </PageLayout>
  );
};

export default FolderFileExplorer;